/*
 * Maïya Paris - mobile-only autoplay for the existing text-with-icons carousel.
 * The native Prestige carousel handles transitions, swipe navigation and dots.
 * Autoplay is opt-in per section and never runs on desktop or in reduced motion.
 */
(() => {
  if (window.__maiyaMobileBenefitsAutoplayLoaded) return;
  window.__maiyaMobileBenefitsAutoplayLoaded = true;

  const mobile = window.matchMedia('(max-width: 699px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const active = new Map();

  function initialize(carousel) {
    if (active.has(carousel)) return;
    const nav = carousel.parentElement?.querySelector('carousel-navigation[aria-controls="' + carousel.id + '"]');
    const dots = Array.from(nav?.querySelectorAll('button') || []);
    const slides = Array.from(carousel.querySelectorAll('.text-with-icons__item'));
    if (dots.length < 2 || slides.length !== dots.length) return;

    let visible = !('IntersectionObserver' in window);
    let userPaused = false;
    let timer = null;

    const stop = () => {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
    };

    const advance = () => {
      if (!carousel.isConnected) {
        stop();
        return;
      }
      const selected = slides.findIndex(slide => slide.classList.contains('is-selected'));
      const dotSelected = dots.findIndex(dot => dot.getAttribute('aria-current') === 'true');
      const current = selected >= 0 ? selected : dotSelected;
      const nextIndex = ((current < 0 ? 0 : current) + 1) % dots.length;
      // Use the theme's native navigation so swipe, dots and selected states stay in sync.
      dots[nextIndex].click();
    };

    const sync = () => {
      const canPlay = mobile.matches && !reduceMotion.matches && !document.hidden
        && visible && !userPaused && carousel.isConnected;
      if (!canPlay) {
        stop();
      } else if (timer === null) {
        timer = window.setInterval(advance, 4000);
      }
    };

    const pauseOnUserAction = event => {
      if (!event.isTrusted) return;
      userPaused = true;
      sync();
    };

    for (const element of [carousel, nav]) {
      element.addEventListener('pointerdown', pauseOnUserAction);
      element.addEventListener('touchstart', pauseOnUserAction, { passive: true });
      element.addEventListener('keydown', pauseOnUserAction);
      element.addEventListener('focusin', pauseOnUserAction);
    }

    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver(entries => {
          visible = entries.some(entry => entry.isIntersecting);
          sync();
        }, { threshold: 0.2 })
      : null;
    observer?.observe(carousel);

    mobile.addEventListener('change', sync);
    reduceMotion.addEventListener('change', sync);
    document.addEventListener('visibilitychange', sync);

    active.set(carousel, () => {
      stop();
      observer?.disconnect();
      mobile.removeEventListener('change', sync);
      reduceMotion.removeEventListener('change', sync);
      document.removeEventListener('visibilitychange', sync);
      for (const element of [carousel, nav]) {
        element.removeEventListener('pointerdown', pauseOnUserAction);
        element.removeEventListener('touchstart', pauseOnUserAction);
        element.removeEventListener('keydown', pauseOnUserAction);
        element.removeEventListener('focusin', pauseOnUserAction);
      }
    });
    sync();
  }

  function scan(root = document) {
    root.querySelectorAll('text-with-icons-carousel[data-maiya-mobile-autoplay="true"]').forEach(initialize);
  }

  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [carousel, teardown] of active.entries()) {
      if (event.target.contains(carousel)) {
        teardown();
        active.delete(carousel);
      }
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  } else {
    scan();
  }
})();
